import React, { useState, useEffect } from "react";
import { useGetDemos, useSendMeetingInvite } from "../hooks/useDemoApi";
import { DemoList } from "../components/demos/DemoList";
import Container from "../components/ui/Container";
import Loader from "../components/ui/Loader";
import { useAuth } from "../hooks/AuthContext";
import { toast } from "react-toastify";
import { SendInviteModal } from "../components/demos/SendInviteModal";
import { toEpochSeconds } from "../lib/dateUtils";

export default function DemoReportsPage() {
  const { user } = useAuth();
  const isEmployee = user?.role === "employee";
  const [page, setPage] = useState(1);
  const limit = 10;

  // Filters (HR/Admin)
  const [searchInput, setSearchInput] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchFilter(searchInput.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // Convert dates to epoch
  const startDate = toEpochSeconds(fromDate);
  const endDate = toEpochSeconds(toDate ? toDate + "T23:59:59" : "");

  // Pass filters as an object
  const filters = {
    search: searchFilter,
    status: statusFilter,
    startDate,
    endDate,
  };

  const { data: demoData, isLoading: isLoadingDemos } = useGetDemos(
    page,
    limit,
    filters
  );

  const totalPages = demoData?.pages || 1;

  const onPageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) setPage(newPage);
  };

  // Invite modal logic ...
  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const [demosForInvite, setDemosForInvite] = useState([]);
  const { mutate: sendInvites, isPending: isSendingInvites } = useSendMeetingInvite();

  const handleOpenInviteModal = (selectedIds) => {
    if (!selectedIds.length) return;
    const selectedDemos = demoData?.demos?.filter((d) => selectedIds.includes(d._id));
    setDemosForInvite(selectedDemos);
    setInviteModalOpen(true);
  };

  const handleInviteSubmit = async (formData) => {
    const demoIds = demosForInvite.map((d) => d._id);
    const payload = { demoIds, ...formData, sender: user?.employeeProfile?._id };

    sendInvites(payload, {
      onSuccess: (data) => {
        toast.success(data.message);
        setInviteModalOpen(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || "Failed to send invites.");
      },
    });
  };

  return (
    <Container>
      <h1 className="text-xl font-bold mb-2">Function Reports</h1>

      {/* Filters (only for HR/Admin) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="flex flex-col">
          <label className="text-sm text-gray-600 mb-1 font-semibold">Search Function</label>
          <input
            type="text"
            placeholder="Search by Demo Name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="border px-3 py-2 rounded-md"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-sm text-gray-600 mb-1 font-semibold">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border px-3 py-2 rounded-md"
          >
            <option value="">All</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-sm text-gray-600 mb-1 font-semibold">From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border px-3 py-2 rounded-md"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-sm text-gray-600 mb-1 font-semibold">To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border px-3 py-2 rounded-md"
          />
        </div>
      </div>

      {/* Render table or loader */}
      {isLoadingDemos ? (
        <Loader />
      ) : (
        <DemoList
          demos={demoData?.demos || []}
          statusFilter={["pending", "completed"]}
          paginationData={{ page, pages: totalPages }}
          onPageChange={onPageChange}
          onSendInviteClick={handleOpenInviteModal}
          isHrView={!isEmployee}
        />
      )}

      {/* Invite modal */}
      {isInviteModalOpen && (
        <SendInviteModal
          demos={demosForInvite}
          onClose={() => setInviteModalOpen(false)}
          onSubmit={handleInviteSubmit}
          isLoading={isSendingInvites}
        />
      )}
    </Container>
  );
}
